import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { NgClass, NgForOf, NgIf, NgOptimizedImage } from '@angular/common';
import { Application, ApplicationConnectionStatus } from '../../models/application.model';
import { FilterPermissionsPipe } from '../../pipes/filter-permissions.pipe';
import { FormatHubAllowancesPipe } from '../../pipes/format-hub-allowances.pipe';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  emptyQuotaOverridesForm,
  QuotaOverridesComponent,
  QuotaOverridesForm,
  quotaOverridesError,
  quotaOverridesFormOf,
  replacementQuotaOverrides,
  requestedQuotaOverrides,
} from './quota-overrides/quota-overrides.component';

// One tab per connection status, plus the applications not yet requested.
type ApplicationsTab = Exclude<ApplicationConnectionStatus, ''> | 'available';

// What the agency uses and can request first, then the requests that are not (or no longer) in use.
const TABS: { key: ApplicationsTab; label: string; empty: string }[] = [
  { key: 'active', label: 'Active', empty: 'No active applications' },
  { key: 'available', label: 'Available', empty: 'No available applications' },
  { key: 'pending', label: 'Pending', empty: 'No pending requests' },
  { key: 'suspended', label: 'Suspended', empty: 'No suspended applications' },
  { key: 'rejected', label: 'Rejected', empty: 'No rejected requests' },
];

const DEFAULT_TAB: ApplicationsTab = 'active';

// The failures of a request by App ID the agency can act on, by HTTP status.
const requestApplicationErrors: Record<number, string> = {
  400: 'This application has already been requested',
  404: 'No application was found with this App ID',
};

// The failures of a quota edit the agency can act on, by HTTP status.
const updateQuotaErrors: Record<number, string> = {
  404: 'This application is no longer enabled',
};

@Component({
  selector: 'app-applications',
  imports: [
    NgIf,
    NgxSpinnerComponent,
    NgForOf,
    NgClass,
    FilterPermissionsPipe,
    FormatHubAllowancesPipe,
    NgOptimizedImage,
    FormsModule,
    QuotaOverridesComponent,
  ],
  templateUrl: './applications.component.html',
  styleUrl: './applications.component.scss',
})
export class ApplicationsComponent implements OnInit {
  loaded: boolean = false;
  // Grouped once per load, so the template doesn't refilter the list on every change detection.
  appsByTab = this.groupByTab([]);
  readonly tabs = TABS;
  activeTab: ApplicationsTab = DEFAULT_TAB;
  selectedApp: Application | null = null;
  modalVisible = false;
  updating = false;
  // The overrides an application requested from its card starts on.
  requestQuotaForm = emptyQuotaOverridesForm();
  appId = '';
  // The overrides an application requested by App ID starts on.
  appIdQuotaForm = emptyQuotaOverridesForm();
  requesting = false;
  quotaApp: Application | null = null;
  quotaForm = emptyQuotaOverridesForm();
  savingQuota = false;

  constructor(
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    this.activeTab = TABS.find(t => t.key === tab)?.key ?? DEFAULT_TAB;
    this.updateTabInUrl(this.activeTab);

    this.getApplications();
  }

  getApplications() {
    this.loaded = false;
    this.spinner.show();

    this.apiService
      .getApplications()
      .pipe(
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: data => {
          this.appsByTab = this.groupByTab(data ?? []);
        },
        error: error => {
          console.error('Failed to load applications:', error);
        },
      });
  }

  // An enabled application without a status predates the statuses, so it counts as active.
  tabOf(app: Application): ApplicationsTab {
    return app.enabled ? app.status || 'active' : 'available';
  }

  private groupByTab(apps: Application[]): Record<ApplicationsTab, Application[]> {
    return Object.fromEntries(
      TABS.map(t => [t.key, apps.filter(app => this.tabOf(app) === t.key)])
    ) as Record<ApplicationsTab, Application[]>;
  }

  get activeTabEmpty(): string {
    return TABS.find(t => t.key === this.activeTab)!.empty;
  }

  setTab(tab: ApplicationsTab): void {
    this.activeTab = tab;
    this.updateTabInUrl(tab);
  }

  updateTabInUrl(tab: ApplicationsTab): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: tab === DEFAULT_TAB ? { tab: null } : { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  confirmToggle() {
    const app = this.selectedApp;
    // The modal stays open until the answer, so a second click must not send the change again.
    if (!app || this.updating) return;
    if (!app.enabled && quotaOverridesError(this.requestQuotaForm)) return;

    // Only a request takes quota overrides; a drop takes the connection's with it.
    const payload = app.enabled
      ? { id: app.data.id, status: 'disable' }
      : { id: app.data.id, status: 'enable', ...requestedQuotaOverrides(this.requestQuotaForm) };
    this.updating = true;
    this.apiService
      .updateApplication(payload)
      .pipe(finalize(() => (this.updating = false)))
      .subscribe({
        next: data => {
          // A request starts pending, so follow it there; a dropped connection leaves the tab it
          // was on.
          if (!app.enabled) this.setTab('pending');
          this.getApplications();
          this.toastr.success(this.toggleSuccessMessage(app, data.message), 'Success');
          this.closeModal();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to update application status:', err);
          // The global error toast is skipped for this request, so the backend's own message
          // stands in for the generic one.
          this.toastr.error(err.error?.error ?? 'Failed to update application', 'Error');
          this.closeModal();
        },
      });
  }

  // Requests an application by its App ID, which reaches private applications the list does not
  // offer. The request then shows under the pending tab until the developer approves it.
  requestApplication(): void {
    const agwId = this.appId.trim();
    if (!agwId || this.requesting || quotaOverridesError(this.appIdQuotaForm)) return;

    this.requesting = true;
    this.apiService
      .requestApplication({ agw_id: agwId, ...requestedQuotaOverrides(this.appIdQuotaForm) })
      .pipe(finalize(() => (this.requesting = false)))
      .subscribe({
        next: data => {
          this.appId = '';
          this.appIdQuotaForm = emptyQuotaOverridesForm();
          this.toastr.success(data.message, 'Success');
          this.setTab('pending');
          this.getApplications();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to request application:', err);
          // The global error toast is skipped for this request, so the backend's own message stands in
          // for the failures not worded here.
          const message =
            requestApplicationErrors[err.status] ??
            err.error?.error ??
            'Failed to request application';
          this.toastr.error(message, 'Error');
        },
      });
  }

  openConfirmationModal(app: Application): void {
    this.selectedApp = app;
    this.requestQuotaForm = emptyQuotaOverridesForm();
    this.modalVisible = true;
  }

  closeModal(): void {
    this.modalVisible = false;
    this.selectedApp = null;
  }

  // Whether the form can be sent, for the buttons that send it.
  quotaFormValid(form: QuotaOverridesForm): boolean {
    return quotaOverridesError(form) === null;
  }

  // A rejected request is only there to be removed, so its quota is neither shown nor edited.
  hasQuota(app: Application): boolean {
    return !!app.quota && app.status !== 'rejected';
  }

  openQuotaModal(app: Application): void {
    if (!app.quota) return;

    this.quotaApp = app;
    this.quotaForm = quotaOverridesFormOf(app.quota.overrides);
  }

  closeQuotaModal(): void {
    this.quotaApp = null;
  }

  // Replaces the connection's overrides with the form's. A changed maximum applies to the key's
  // remaining quota at once.
  saveQuota(): void {
    const app = this.quotaApp;
    if (!app || this.savingQuota || quotaOverridesError(this.quotaForm)) return;

    this.savingQuota = true;
    this.apiService
      .updateApplicationQuota({ id: app.data.id, ...replacementQuotaOverrides(this.quotaForm) })
      .pipe(finalize(() => (this.savingQuota = false)))
      .subscribe({
        next: () => {
          this.toastr.success(`Quota for ${app.data.label} updated.`, 'Success');
          this.closeQuotaModal();
          this.getApplications();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to update application quota:', err);
          // The global error toast is skipped for this request, as for a request by App ID.
          const message =
            updateQuotaErrors[err.status] ?? err.error?.error ?? 'Failed to update quota';
          this.toastr.error(message, 'Error');
        },
      });
  }

  // The quota_renewal_rate is the searches a booking earns back, which is a look-to-book ratio.
  formatRatio(renewalRate: number): string {
    return renewalRate === 0
      ? 'No searches earned back'
      : `${renewalRate.toLocaleString('en-US')} : 1`;
  }

  // "Every 1 day" reads awkwardly, so a single day is given in hours.
  formatResetPeriod(days: number): string {
    return days === 1 ? '24 hours' : `${days.toLocaleString('en-US')} days`;
  }

  // A suspension is the developer's to lift, and revoking would only lose the key and the approval.
  // A rejected request can be removed, which is how the agency gets to request the application again.
  canRevoke(app: Application): boolean {
    return app.status !== 'suspended';
  }

  revokeLabel(app: Application): string {
    switch (app.status) {
      case 'pending':
        return 'Cancel Request';
      case 'rejected':
        return 'Remove';
      default:
        return 'Revoke';
    }
  }

  // The backend words every drop as a revocation, which a pending or rejected request never was.
  private toggleSuccessMessage(app: Application, backendMessage: string): string {
    switch (app.status) {
      case 'pending':
        return `Request for ${app.data.label} cancelled.`;
      case 'rejected':
        return `${app.data.label} removed.`;
      default:
        return backendMessage;
    }
  }

  get confirmationMessage(): string {
    if (!this.selectedApp) return '';
    if (!this.selectedApp.enabled) {
      return 'By activating this application you are requesting to grant them access to your airline content for shopping, booking and issuing orders. Access starts once the application’s developer approves the request, and can be revoked anytime. Are you sure you want to do this?';
    }
    if (this.selectedApp.status === 'pending') {
      return 'By cancelling this request, the application won’t be given access to your airline content. Are you sure you want to do this?';
    }
    if (this.selectedApp.status === 'rejected') {
      return 'This removes the rejected request, so you can request the application again. Are you sure you want to do this?';
    }
    return 'By revoking this, the application won’t be able to access your airline content anymore. Are you sure you want to do this?';
  }
}
